import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

export function LossCurve() {
  const [lr, setLr] = useState(35);
  const [reg, setReg] = useState(false);

  const { train, val, diag, msg, rate } = useMemo(() => {
    const rate = lr / 100;
    const tr = [];
    const va = [];
    for (let i = 0; i <= 46; i++) {
      const x = i / 46;
      let t, v;
      if (rate < 0.12) {
        t = 1 - 0.12 * x;
        v = 1 - 0.1 * x;
      } else if (rate > 0.78) {
        const osc = Math.sin(i * 0.9) * 0.28 * (1 + x);
        t = Math.min(1.15, 0.75 + osc * 0.6 + x * 0.25);
        v = Math.min(1.2, 0.8 + osc * 0.7 + x * 0.3);
      } else {
        t = Math.exp(-3.2 * x) * 0.95 + 0.03;
        v = reg
          ? Math.exp(-2.7 * x) * 0.95 + 0.14
          : Math.exp(-3.0 * x) * 0.9 + 0.1 + Math.max(0, x - 0.42) * 0.85;
      }
      tr.push([40 + x * 455, 160 - Math.max(0, Math.min(1.2, t)) * 125]);
      va.push([40 + x * 455, 160 - Math.max(0, Math.min(1.2, v)) * 125]);
    }
    const d = (a) => a.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
    let diag, msg;
    if (rate < 0.12) {
      diag = "Loss is flat. Steps are too small, so the model shuffles when it should stride.";
      msg = "→ INCREASE the learning rate.";
    } else if (rate > 0.78) {
      diag = "Loss is oscillating / exploding. Steps are too big, so it overshoots every time.";
      msg = "→ DECREASE the learning rate.";
    } else if (!reg) {
      diag = "Training loss keeps falling while validation loss turns upward. That gap is memorization.";
      msg = "→ OVERFITTING: turn Regularization on (or max_depth↓, early stopping, more data). Never \"train longer\".";
    } else {
      diag = "Healthy. Both curves fall and stay close together.";
      msg = "→ This is what you want: learning, not memorizing.";
    }
    return { train: d(tr), val: d(va), diag, msg, rate };
  }, [lr, reg]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="my-12 border-2 border-[#2F6F4F] bg-white/70 backdrop-blur-sm p-7 md:p-9"
      data-testid="widget-loss-curve"
    >
      <div className="font-mono-ui text-[10px] font-bold uppercase tracking-[0.22em] text-[#2F6F4F] mb-6">
        Try it · Watch the loss curve misbehave
      </div>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-4 text-[13px]">
        <label className="flex items-center gap-3">
          <span className="font-mono-ui text-[11px] uppercase tracking-[0.1em]">Learning rate</span>
          <input
            type="range"
            min="0"
            max="100"
            value={lr}
            onChange={(e) => setLr(+e.target.value)}
            className="ink-range w-40"
            data-testid="loss-lr-slider"
          />
          <span className="font-mono-ui tabular-nums text-[12px]">{(rate * 0.2).toFixed(3)}</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={reg}
            onChange={(e) => setReg(e.target.checked)}
            className="accent-[#2F6F4F] w-4 h-4"
            data-testid="loss-reg-checkbox"
          />
          <span className="font-mono-ui text-[11px] uppercase tracking-[0.1em]">Regularization on</span>
        </label>
      </div>
      <svg viewBox="0 0 520 190" className="w-full h-auto">
        <line x1="40" y1="160" x2="500" y2="160" stroke="var(--border-heavy)" />
        <line x1="40" y1="10" x2="40" y2="160" stroke="var(--border-heavy)" />
        <text x="14" y="90" fontSize="10" fill="#6b7365" transform="rotate(-90 14,90)" className="font-mono-ui">loss</text>
        <text x="255" y="182" fontSize="10" fill="#6b7365" className="font-mono-ui">epochs</text>
        <motion.path d={train} fill="none" stroke="#2F6F4F" strokeWidth="2.5" animate={{ d: train }} transition={{ duration: 0.3 }} />
        <motion.path d={val} fill="none" stroke="#D26040" strokeWidth="2.5" strokeDasharray="5 3" animate={{ d: val }} transition={{ duration: 0.3 }} />
        <text x="388" y="22" fontSize="11" fill="#2F6F4F" className="font-mono-ui">training loss</text>
        <text x="388" y="36" fontSize="11" fill="#D26040" className="font-mono-ui">validation loss</text>
      </svg>
      <div className="mt-4 bg-black/[0.03] px-4 py-3 text-[13.5px] leading-relaxed" data-testid="loss-output">
        <strong className="text-[#1C2430]">{diag}</strong>
        <br />
        <span className="text-[#1C2430]/75">{msg}</span>
      </div>
    </motion.div>
  );
}
